import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CreateAzureGroupScopeMappingRequest,
  UpdateAzureGroupScopeMappingRequest,
} from 'src/api/FiretrolApiClient';
import { azureGroupSyncApiClient } from 'src/api/FiretrolApiClientInstances';
import { invalidateFor } from 'src/api/queries/invalidationMap';

export function useCreateAzureGroupSyncMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateAzureGroupScopeMappingRequest) =>
      azureGroupSyncApiClient.createMapping(request),
    meta: { successMessage: 'Mapping created!', suppressErrorToast: true },
    onSettled: () => invalidateFor('azureGroupSync.save', qc),
  });
}

export interface UpdateAzureGroupSyncMappingArgs {
  id: string;
  request: UpdateAzureGroupScopeMappingRequest;
}

export function useUpdateAzureGroupSyncMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: UpdateAzureGroupSyncMappingArgs) =>
      azureGroupSyncApiClient.updateMapping(id, request),
    meta: { successMessage: 'Mapping updated!', suppressErrorToast: true },
    onSettled: () => invalidateFor('azureGroupSync.save', qc),
  });
}

export function useDeleteAzureGroupSyncMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => azureGroupSyncApiClient.deleteMapping(id),
    meta: {
      successMessage: 'Mapping deleted',
      errorContext: 'delete Azure group mapping',
    },
    onSettled: () => invalidateFor('azureGroupSync.save', qc),
  });
}

/**
 * A connectivity probe: the server reports failure in the payload rather than
 * by status code, so the caller renders the outcome itself.
 */
export function useTestAzureGroupSyncConnection() {
  return useMutation({
    mutationFn: () => azureGroupSyncApiClient.testConnection(),
    meta: { errorContext: 'Azure AD test connection' },
  });
}

export function useAzureGroupSyncFullSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => azureGroupSyncApiClient.fullSync(),
    meta: {
      successMessage: 'Full sync triggered successfully',
      errorContext: 'Azure AD full sync',
    },
    onSettled: () => invalidateFor('azureGroupSync.save', qc),
  });
}
